import { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  RefreshCw,
  ArrowDownToLine,
  ArrowUpFromLine,
  Link2,
  Unlink,
  X,
  Loader,
  Check,
  AlertCircle,
} from 'lucide-react';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
import Badge from '../components/common/Badge';
import {
  getProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  getStripeStatus,
  connectStripe,
  disconnectStripe,
  syncFromStripe,
  pushToStripe,
  pushAllToStripe,
  syncBidirectional,
} from '../services/api';
import type { Product, ProductType, BillingInterval, StripeConnectionStatus } from '../types';

// ── Helpers ─────────────────────────────────────────────────────────

function formatPrice(cents: number, currency: string, interval?: BillingInterval): string {
  const dollars = (cents / 100).toFixed(2);
  const symbol = currency === 'USD' ? '$' : currency;
  let suffix = '';
  if (interval) {
    const map: Record<string, string> = { MONTHLY: '/mo', YEARLY: '/yr', QUARTERLY: '/qtr' };
    suffix = map[interval] || '';
  }
  return `${symbol}${dollars}${suffix}`;
}

const typeVariant: Record<ProductType, { label: string; variant: string }> = {
  ONE_TIME: { label: 'One-Time', variant: 'info' },
  RECURRING: { label: 'Recurring', variant: 'success' },
  USAGE_BASED: { label: 'Usage', variant: 'sage' },
};

// ── Empty form state ────────────────────────────────────────────────

interface ProductForm {
  name: string;
  sku: string;
  description: string;
  product_type: ProductType;
  unit_price_cents: number;
  currency: string;
  billing_interval: BillingInterval | '';
  category: string;
  is_active: boolean;
}

const emptyForm: ProductForm = {
  name: '',
  sku: '',
  description: '',
  product_type: 'ONE_TIME',
  unit_price_cents: 0,
  currency: 'USD',
  billing_interval: '',
  category: '',
  is_active: true,
};

// ── Component ───────────────────────────────────────────────────────

export default function ProductsPage() {
  // Data state
  const [products, setProducts] = useState<Product[]>([]);
  const [stripeStatus, setStripeStatus] = useState<StripeConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Search & filter
  const [search, setSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form, setForm] = useState<ProductForm>(emptyForm);
  const [saving, setSaving] = useState(false);

  // Stripe connect modal
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [connecting, setConnecting] = useState(false);

  // Stripe sync state
  const [syncing, setSyncing] = useState('');

  // ── Data loading ────────────────────────────────────────────────────

  const loadProducts = useCallback(async () => {
    try {
      const data = await getProducts();
      setProducts(data.products || []);
    } catch (err) {
      console.error('Failed to load products:', err);
      setError('Failed to load products');
    }
  }, []);

  const loadStripeStatus = useCallback(async () => {
    try {
      const data = await getStripeStatus();
      setStripeStatus(data);
    } catch (err) {
      console.error('Failed to load Stripe status:', err);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([loadProducts(), loadStripeStatus()]);
      setLoading(false);
    };
    load();
  }, [loadProducts, loadStripeStatus]);

  // ── Filtered products ───────────────────────────────────────────────

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  // ── Product CRUD ────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingProduct(null);
    setForm(emptyForm);
    setPriceInput('0.00');
    setModalOpen(true);
  };

  const openEditModal = (product: Product) => {
    setEditingProduct(product);
    setForm({
      name: product.name,
      sku: product.sku || '',
      description: product.description || '',
      product_type: product.product_type,
      unit_price_cents: product.unit_price_cents,
      currency: product.currency,
      billing_interval: product.billing_interval || '',
      category: product.category || '',
      is_active: product.is_active,
    });
    setPriceInput((product.unit_price_cents / 100).toFixed(2));
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        sku: form.sku || undefined,
        description: form.description || undefined,
        product_type: form.product_type,
        unit_price_cents: form.unit_price_cents,
        currency: form.currency,
        billing_interval: form.product_type === 'RECURRING' && form.billing_interval ? form.billing_interval : undefined,
        category: form.category || undefined,
        is_active: form.is_active,
      };

      if (editingProduct) {
        await updateProduct(editingProduct.id, payload);
      } else {
        await createProduct(payload);
      }

      setModalOpen(false);
      await loadProducts();
    } catch (err) {
      console.error('Save failed:', err);
      setError('Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try {
      await deleteProduct(id);
      await loadProducts();
    } catch (err) {
      console.error('Delete failed:', err);
      setError('Failed to delete product');
    }
  };

  // ── Stripe actions ──────────────────────────────────────────────────

  const handleConnect = async () => {
    if (!apiKeyInput.trim()) return;
    if (!apiKeyInput.trim().startsWith('sk_')) {
      setError('Invalid Stripe key format. Key must start with sk_live_ or sk_test_');
      return;
    }
    setConnecting(true);
    try {
      const data = await connectStripe(apiKeyInput.trim());
      setStripeStatus(data);
      setConnectModalOpen(false);
      setApiKeyInput('');
    } catch (err) {
      console.error('Connect failed:', err);
      setError('Failed to connect Stripe');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await disconnectStripe();
      setStripeStatus({ connected: false });
    } catch (err) {
      console.error('Disconnect failed:', err);
    }
  };

  const handleSync = async (type: 'pull' | 'push' | 'full') => {
    setSyncing(type);
    try {
      if (type === 'pull') await syncFromStripe();
      else if (type === 'push') await pushAllToStripe();
      else await syncBidirectional();
      await Promise.all([loadProducts(), loadStripeStatus()]);
    } catch (err) {
      console.error('Sync failed:', err);
      setError(`Stripe ${type} sync failed`);
    } finally {
      setSyncing('');
    }
  };

  const handlePushOne = async (productId: string) => {
    setSyncing(productId);
    try {
      await pushToStripe(productId);
      const data = await getProducts();
      setProducts(data.products || []);
      if (editingProduct?.id === productId) {
        const updated = (data.products || []).find((p: Product) => p.id === productId);
        if (updated) setEditingProduct(updated);
      }
    } catch (err) {
      console.error('Push failed:', err);
      setError('Failed to push product to Stripe');
    } finally {
      setSyncing('');
    }
  };

  // ── Dollar input helpers ────────────────────────────────────────────

  const [priceInput, setPriceInput] = useState('0.00');

  const handlePriceBlur = () => {
    const dollars = parseFloat(priceInput.replace(/[^0-9.]/g, ''));
    const cents = isNaN(dollars) ? 0 : Math.round(dollars * 100);
    setForm((f) => ({ ...f, unit_price_cents: cents }));
    setPriceInput((cents / 100).toFixed(2));
  };

  // ── Render ──────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="page-enter">
        <Header title="Products" subtitle="Manage products, pricing, and Stripe sync" />
        <div className="flex items-center justify-center py-20">
          <Loader size={24} className="animate-spin" style={{ color: 'var(--netrun-green)' }} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header title="Products" subtitle="Manage products, pricing, and Stripe sync" />

      <div className="p-4 md:p-8 space-y-6">
        {/* Error banner */}
        {error && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm"
            style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--error)' }}
          >
            <AlertCircle size={16} />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto cursor-pointer" style={{ background: 'none', border: 'none', color: 'var(--error)' }}>
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Section A: Stripe Connection Status ──────────────────────── */}
        <Panel
          title="Stripe Integration"
          action={
            stripeStatus?.connected ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSync('pull')}
                  disabled={!!syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                >
                  {syncing === 'pull' ? <Loader size={12} className="animate-spin" /> : <ArrowDownToLine size={12} />}
                  Pull
                </button>
                <button
                  onClick={() => handleSync('push')}
                  disabled={!!syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                >
                  {syncing === 'push' ? <Loader size={12} className="animate-spin" /> : <ArrowUpFromLine size={12} />}
                  Push
                </button>
                <button
                  onClick={() => handleSync('full')}
                  disabled={!!syncing}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
                  style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)' }}
                >
                  {syncing === 'full' ? <Loader size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                  Full Sync
                </button>
              </div>
            ) : undefined
          }
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Status dot */}
              <div
                className="w-2.5 h-2.5 rounded-full"
                style={{ background: stripeStatus?.connected ? 'var(--success)' : 'var(--error)' }}
              />
              <div>
                <p className="text-sm font-body" style={{ color: 'var(--text-primary)' }}>
                  {stripeStatus?.connected ? 'Connected' : 'Not Connected'}
                </p>
                {stripeStatus?.connected && stripeStatus.account_name && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                    {stripeStatus.account_name}
                    {stripeStatus.last_sync && ` — Last sync: ${new Date(stripeStatus.last_sync).toLocaleString()}`}
                  </p>
                )}
              </div>
            </div>

            {stripeStatus?.connected ? (
              <button
                onClick={handleDisconnect}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
                style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--error)' }}
              >
                <Unlink size={12} />
                Disconnect
              </button>
            ) : (
              <button
                onClick={() => setConnectModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
                style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)' }}
              >
                <Link2 size={12} />
                Connect Stripe
              </button>
            )}
          </div>
        </Panel>

        {/* ── Section B: Products Table ─────────────────────────────────── */}

        {/* Toolbar */}
        <div className="flex items-center justify-between">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              type="text"
              placeholder="Search products..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-lg text-sm w-[320px] outline-none"
              style={{
                background: 'var(--bg-input)',
                border: '1px solid var(--border-primary)',
                color: 'var(--text-primary)',
              }}
            />
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-body cursor-pointer transition-all"
            style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)' }}
          >
            <Plus size={16} />
            Add Product
          </button>
        </div>

        {/* Table */}
        <Panel noPadding className="card-interactive">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                  {['Name', 'SKU', 'Type', 'Price', 'Category', 'Stripe', 'Active', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 font-body text-xs"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-sm" style={{ color: 'var(--text-muted)' }}>
                      {search ? 'No products match your search' : 'No products yet — click "Add Product" to create one'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((product) => (
                    <tr
                      key={product.id}
                      className="cursor-pointer transition-all duration-150"
                      style={{ borderBottom: '1px solid var(--border-primary)' }}
                      onClick={() => openEditModal(product)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--bg-input)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td className="px-5 py-3.5" style={{ color: 'var(--text-primary)' }}>
                        {product.name}
                      </td>
                      <td className="px-5 py-3.5" style={{ color: 'var(--text-muted)' }}>
                        {product.sku || '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        <Badge
                          label={typeVariant[product.product_type].label}
                          variant={typeVariant[product.product_type].variant as any}
                        />
                      </td>
                      <td className="px-5 py-3.5 font-body" style={{ color: 'var(--text-primary)' }}>
                        {formatPrice(
                          product.unit_price_cents,
                          product.currency,
                          product.product_type === 'RECURRING' ? product.billing_interval : undefined
                        )}
                      </td>
                      <td className="px-5 py-3.5" style={{ color: 'var(--text-secondary)' }}>
                        {product.category || '—'}
                      </td>
                      <td className="px-5 py-3.5">
                        {product.stripe_product_id ? (
                          <div className="flex items-center gap-1.5">
                            <Check size={14} style={{ color: 'var(--success)' }} />
                            <span className="text-xs" style={{ color: 'var(--success)' }}>Linked</span>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePushOne(product.id);
                            }}
                            disabled={!!syncing || !stripeStatus?.connected}
                            className="flex items-center gap-1 text-xs cursor-pointer transition-all"
                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
                            title={stripeStatus?.connected ? 'Push to Stripe' : 'Connect Stripe first'}
                          >
                            {syncing === product.id ? (
                              <Loader size={12} className="animate-spin" />
                            ) : (
                              <ArrowUpFromLine size={12} />
                            )}
                            Push
                          </button>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <div
                          className="w-8 h-4.5 rounded-full relative cursor-pointer"
                          style={{
                            background: product.is_active ? 'var(--netrun-green)' : 'var(--bg-input)',
                            border: `1px solid ${product.is_active ? 'var(--netrun-green)' : 'var(--border-subtle)'}`,
                            width: '32px',
                            height: '18px',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            updateProduct(product.id, { is_active: !product.is_active })
                              .then(() => loadProducts())
                              .catch(() => setError('Failed to update product status'));
                          }}
                        >
                          <div
                            className="absolute top-0.5 rounded-full transition-all duration-200"
                            style={{
                              width: '12px',
                              height: '12px',
                              left: product.is_active ? '16px' : '2px',
                              background: product.is_active ? 'var(--netrun-black)' : 'var(--text-muted)',
                            }}
                          />
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(product.id);
                          }}
                          className="text-xs cursor-pointer transition-all"
                          style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
                          title="Delete product"
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {/* ── Connect Stripe Modal ─────────────────────────────────────── */}
      {connectModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={() => { setConnectModalOpen(false); setApiKeyInput(''); }}
        >
          <div
            className="rounded-xl w-full max-w-md slide-up"
            style={{
              background: '#141414',
              border: '1px solid var(--border-primary)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: '1px solid var(--border-primary)' }}
            >
              <h3 className="font-display text-sm" style={{ color: 'var(--text-primary)' }}>
                Connect Stripe
              </h3>
              <button
                onClick={() => { setConnectModalOpen(false); setApiKeyInput(''); }}
                className="cursor-pointer"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                  Stripe Secret API Key
                </label>
                <input
                  type="password"
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder="sk_live_..."
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setConnectModalOpen(false); setApiKeyInput(''); }}
                  className="px-4 py-2 rounded-lg text-sm cursor-pointer transition-all"
                  style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConnect}
                  disabled={connecting || !apiKeyInput.trim()}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer transition-all"
                  style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)', opacity: connecting || !apiKeyInput.trim() ? 0.5 : 1 }}
                >
                  {connecting && <Loader size={14} className="animate-spin" />}
                  Connect
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Product Modal ──────────────────────────────── */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto slide-up"
            style={{
              background: '#141414',
              border: '1px solid var(--border-primary)',
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div
              className="flex items-center justify-between px-5 py-4"
              style={{ borderBottom: '1px solid var(--border-primary)' }}
            >
              <h3 className="font-display text-sm" style={{ color: 'var(--text-primary)' }}>
                {editingProduct ? 'Edit Product' : 'New Product'}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="cursor-pointer"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)' }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Form */}
            <div className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
                />
              </div>

              {/* SKU */}
              <div>
                <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>SKU</label>
                <input
                  type="text"
                  value={form.sku}
                  onChange={(e) => setForm((f) => ({ ...f, sku: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
                  style={{
                    background: 'var(--bg-input)',
                    border: '1px solid var(--border-primary)',
                    color: 'var(--text-primary)',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
                />
              </div>

              {/* Product Type + Price row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Type</label>
                  <select
                    value={form.product_type}
                    onChange={(e) => setForm((f) => ({ ...f, product_type: e.target.value as ProductType }))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none cursor-pointer"
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="ONE_TIME">One-Time</option>
                    <option value="RECURRING">Recurring</option>
                    <option value="USAGE_BASED">Usage-Based</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Price (USD)</label>
                  <input
                    type="text"
                    value={priceInput}
                    onChange={(e) => setPriceInput(e.target.value)}
                    onBlur={(e) => { handlePriceBlur(); e.currentTarget.style.borderColor = 'var(--border-primary)'; }}
                    placeholder="0.00"
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                  />
                </div>
              </div>

              {/* Billing Interval (only for RECURRING) */}
              {form.product_type === 'RECURRING' && (
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Billing Interval</label>
                  <select
                    value={form.billing_interval}
                    onChange={(e) => setForm((f) => ({ ...f, billing_interval: e.target.value as BillingInterval }))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none cursor-pointer"
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <option value="">Select interval</option>
                    <option value="MONTHLY">Monthly</option>
                    <option value="QUARTERLY">Quarterly</option>
                    <option value="YEARLY">Yearly</option>
                  </select>
                </div>
              )}

              {/* Category + Currency row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Category</label>
                  <input
                    type="text"
                    value={form.category}
                    onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                    placeholder="e.g. SaaS, Service"
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
                  />
                </div>
                <div>
                  <label className="block text-xs mb-1.5" style={{ color: 'var(--text-secondary)' }}>Currency</label>
                  <input
                    type="text"
                    value={form.currency}
                    onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
                    style={{
                      background: 'var(--bg-input)',
                      border: '1px solid var(--border-primary)',
                      color: 'var(--text-primary)',
                    }}
                    onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--netrun-green)')}
                    onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border-primary)')}
                  />
                </div>
              </div>

              {/* Active toggle */}
              <div className="flex items-center justify-between">
                <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>Active</label>
                <div
                  className="rounded-full relative cursor-pointer"
                  style={{
                    background: form.is_active ? 'var(--netrun-green)' : 'var(--bg-input)',
                    border: `1px solid ${form.is_active ? 'var(--netrun-green)' : 'var(--border-subtle)'}`,
                    width: '40px',
                    height: '22px',
                  }}
                  onClick={() => setForm((f) => ({ ...f, is_active: !f.is_active }))}
                >
                  <div
                    className="absolute top-0.5 rounded-full transition-all duration-200"
                    style={{
                      width: '16px',
                      height: '16px',
                      left: form.is_active ? '20px' : '2px',
                      background: form.is_active ? 'var(--netrun-black)' : 'var(--text-muted)',
                    }}
                  />
                </div>
              </div>

              {/* Footer actions */}
              <div
                className="flex items-center justify-between pt-4"
                style={{ borderTop: '1px solid var(--border-primary)' }}
              >
                <div>
                  {editingProduct?.stripe_product_id && stripeStatus?.connected && (
                    <button
                      onClick={() => {
                        handlePushOne(editingProduct.id);
                      }}
                      disabled={!!syncing}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs cursor-pointer transition-all"
                      style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                    >
                      {syncing === editingProduct.id ? <Loader size={12} className="animate-spin" /> : <ArrowUpFromLine size={12} />}
                      Push to Stripe
                    </button>
                  )}
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 rounded-lg text-sm cursor-pointer transition-all"
                    style={{ background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !form.name.trim()}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer transition-all"
                    style={{ background: 'var(--netrun-green)', border: 'none', color: 'var(--netrun-black)', opacity: saving || !form.name.trim() ? 0.5 : 1 }}
                  >
                    {saving && <Loader size={14} className="animate-spin" />}
                    {editingProduct ? 'Save Changes' : 'Create Product'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
